import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Avatar } from 'primereact/avatar';
import { DateTime } from 'luxon';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import { displayCrmStateName, crmStateNameSearchBlob, withCrmStateNameCopySuffix, formatCrmSetupApplicableTypes, normalizeCrmSetupApplicableTypes, crmSetupApplicableTypeKey, crmSetupStateDisplayHex } from '../../utils/crmStateI18n';
import { reloadCrmStatesCatalog, selectActiveCrmStates } from '../../utils/crmCatalogStore';
import { CRM_STATE_PHASES } from '../../utils/crmStatePhases';
import { cleanSorters } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { restoreCrmSetupLocalListMask } from '../../utils/crmSetupListMask';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { SearchIcon } from '../svg/svgIcons';
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, ExportIcon, RefreshIcon, VerticalLineIcon, EditRowIcon, FavoriteIcon, PriceIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon } from '../svg/svgIcons';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';

const CUSTOMER_STATES_LIST_COLUMN_I18N_KEYS = Object.freeze({
  id: 'ServiceTaskTable.Col.Id',
  name: 'ServiceStateAdd.StateName',
  applicable_types: 'ServiceStateAdd.ShowIn',
  phase: 'ServiceStateAdd.Phase',
  transition_type: 'ServiceStateAdd.TransitionType',
  color: 'ServiceStateAdd.StateColor',
  created_at: 'CustomerStatesList.Col.Created',
  updated_at: 'ServiceTaskTable.Col.Updated',
});

const CRM_STATE_TRANSITION_TYPES = [
  { name: 'ServiceState.TransitionType.NonFinal', code: 'non_final' },
  { name: 'ServiceState.TransitionType.LevelFinal', code: 'level_final' },
  { name: 'ServiceState.TransitionType.SectionFinal', code: 'section_final' },
  { name: 'ServiceState.TransitionType.FinalManually', code: 'final_manually' },
  { name: 'ServiceState.TransitionType.FinalAuto', code: 'final_auto' },
  { name: 'ServiceState.TransitionType.Reopen', code: 'reopen' },
];

const PAGE_LIST_KEY = 'CustomerStatesList';
const PAGE_ADD_KEY = 'CustomerStatesAdd';
const MASK_ADD = 'cust009';
const DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  name: true,
  applicable_types: true,
  color: true,
  phase: true,
  transition_type: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE };
  return merged;
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) table.hideColumn?.(f);
    else table.showColumn?.(f);
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function applicableTypeKey(code) {
  return crmSetupApplicableTypeKey(code);
}

export default function CustomerStatesList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    toast, setDialogText, setVisible, openFavoriteDialog, setFastView, setUserMaskFilter, setStandardMaskFilter, openChosenNotificationPreferences,
    getUserInitials, getUserColor, getDateFormat, formatDate, getSlovakColorName,
  } = useContext(UtilContext);
  const tableRef = useRef(null);
  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const getTable = useCallback(() => tableRef.current?.table ?? tableRef.current, []);

  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: CUSTOMER_STATES_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const catalogRows = useMemo(() => selectActiveCrmStates(state), [state.crm_states]);
  const reloadCatalog = useCallback(() => reloadCrmStatesCatalog(dispatch, getRequest), [dispatch]);
  const canAdd = !!user?.permissions?.includes('add customer_management-setup');
  const canEdit = !!user?.permissions?.includes('edit customer_management-setup');
  const canDelete = !!user?.permissions?.includes('delete customer_management-setup');
  const canSetStandardMask = !!user?.permissions?.includes('set standard mask');

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'CustomerStatesList');
    if (!row) return false;
    const attributes = typeof row.attributes === 'string' ? (() => { try { return JSON.parse(row.attributes); } catch { return []; } })() : (row.attributes || []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const savedCols = typeof row.columns === 'string' ? (() => { try { return JSON.parse(row.columns); } catch { return {}; } })() : (row.columns || {});
    const merged = mergeVisibility(savedCols && typeof savedCols === 'object' ? savedCols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const getSelectedRows = useCallback(() => {
    const table = getTable();
    return table?.getSelectedData?.() ?? [];
  }, [getTable]);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    setEditIcon(!(canEdit && count === 1));
    setCopyIcon(!(canAdd && count === 1));
    setDeleteIcon(!(canDelete && count > 0));
  }, [canAdd, canDelete, canEdit]);

  const normalizeText = useCallback(
    (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(),
    [],
  );

  const dateOrUserFilter = useCallback(
    (headerValue, _rowValue, rowData, filterParams) => {
      if (!headerValue || String(headerValue).trim() === '') return true;
      const raw = String(headerValue).trim();
      if (raw.startsWith('range:')) {
        const p = raw.slice('range:'.length).split(':');
        if (p.length >= 2 && p[0] && p[1]) {
          const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
          const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
          const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
          return dt.isValid ? dt >= from && dt <= to : false;
        }
        return true;
      }
      const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const userObj = filterParams?.name === 'created_at' ? rowData?.created_by_user : rowData?.updated_by_user;
      const uname = userObj?.employee?.name || userObj?.username || '';
      const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
      const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat('HH:mm')} ${dt.toFormat('yyyy-MM-dd')}` : '';
      const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      return hay.includes(search);
    },
    [dateFmt],
  );

  const normalizeFilter = useCallback(
    (headerValue, rowValue) => {
      if (headerValue == null || String(headerValue).trim() === '') return true;
      return normalizeText(rowValue).includes(normalizeText(headerValue));
    },
    [normalizeText],
  );

  const typesHeaderFilter = useCallback(
    (headerValue, _rowValue, rowData) => {
      if (headerValue == null || String(headerValue).trim() === '') return true;
      const display = formatCrmSetupApplicableTypes(rowData?.applicable_types, t);
      const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      return norm(display).includes(norm(headerValue));
    },
    [t],
  );

  const phaseHeaderFilter = useCallback(
    (headerValue, _rowValue, rowData) => {
      if (headerValue == null || String(headerValue).trim() === '') return true;
      const option = CRM_STATE_PHASES.find((item) => item.code === rowData?.phase);
      const display = option ? t(option.name) : '';
      const code = String(rowData?.phase ?? '');
      const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const needle = norm(headerValue);
      return norm(display).includes(needle) || norm(code).includes(needle);
    },
    [t],
  );

  const transitionTypeHeaderFilter = useCallback(
    (headerValue, _rowValue, rowData) => {
      if (headerValue == null || String(headerValue).trim() === '') return true;
      const option = CRM_STATE_TRANSITION_TYPES.find((item) => item.code === rowData?.transition_type);
      const display = option ? t(option.name) : '';
      const code = String(rowData?.transition_type ?? '');
      const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const needle = norm(headerValue);
      return norm(display).includes(needle) || norm(code).includes(needle);
    },
    [t],
  );

  const nameHeaderFilter = useCallback(
    (headerValue, _rowValue, rowData) => {
      if (headerValue == null || String(headerValue).trim() === '') return true;
      const display = displayCrmStateName(rowData?.name, locale);
      const blob = crmStateNameSearchBlob(rowData?.name);
      const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const needle = norm(headerValue);
      return norm(display).includes(needle) || norm(blob).includes(needle);
    },
    [locale],
  );

  function openAdd(type, row = null) {
    const translationKey = type === 'add' ? 'CustomerStatesAdd.New' : 'CustomerStatesAdd.TabDetail';
    const legacyLabel = t(translationKey);
    dispatch({
      type: 'create_new_tab',
      name: legacyLabel,
      translationKey,
      legacyLabel,
      page: PAGE_ADD_KEY,
      props: { crm_state: row, type },
      // non-progressive setup pattern: list keeps list mask, detail/add uses add mask
      mask: MASK_ADD,
    });
  }

  const deleteSelected = useCallback(async () => {
    const selected = getSelectedRows();
    await Promise.all(selected.map((row) => deleteRequest(`/api/crm_states/${row.id}`)));
    toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerStatesList.Toast.Deleted'), life: 2500 });
    reloadCatalog();
  }, [getSelectedRows, reloadCatalog, toast]);

  function confirmDelete() {
    if (!canDelete) return;
    const selected = getSelectedRows();
    if (!selected.length) return;
    confirmDialog({
      message: t('CustomerStatesList.DeleteConfirmMessage'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      acceptClassName: 'p-button-danger',
      accept: deleteSelected,
    });
  }

  function onEditSelected() {
    if (!canEdit) return;
    const selected = getSelectedRows();
    if (!selected.length) return;
    openAdd('edit', selected[0]);
  }

  function onCopySelected() {
    if (!canAdd) return;
    const selected = getSelectedRows();
    if (!selected.length) return;
    const row = selected[0];
    openAdd('add', { ...row, id: null, name: withCrmStateNameCopySuffix(row?.name) });
  }

  const applyToolbarSearch = useCallback(
    (value) => {
      const table = tableRef.current?.table ?? tableRef.current;
      if (!table) return;
      const term = String(value || '').trim().toLowerCase();
      if (!term) {
        table.clearFilter(true);
        return;
      }
      table.setFilter((data) => {
        const nameBlob = crmStateNameSearchBlob(data?.name).toLowerCase();
        const nameDisp = String(displayCrmStateName(data?.name, locale) || '').toLowerCase();
        const types = normalizeCrmSetupApplicableTypes(data?.applicable_types);
        const typeLabels = types.map((c) => t(applicableTypeKey(c))).join(' ').toLowerCase();
        const ph = CRM_STATE_PHASES.find((p) => p.code === data?.phase);
        const tr = CRM_STATE_TRANSITION_TYPES.find((p) => p.code === data?.transition_type);
        const phaseTxt = (ph ? t(ph.name) : '').toLowerCase();
        const trTxt = (tr ? t(tr.name) : '').toLowerCase();
        const cu = (data?.created_by_user?.employee?.name || data?.created_by_user?.username || '').toLowerCase();
        const uu = (data?.updated_by_user?.employee?.name || data?.updated_by_user?.username || '').toLowerCase();
        const hay = `${data?.id ?? ''} ${nameBlob} ${nameDisp} ${typeLabels} ${data?.color ?? ''} ${phaseTxt} ${String(data?.phase ?? '').toLowerCase()} ${trTxt} ${String(data?.transition_type ?? '').toLowerCase()} ${cu} ${uu}`.toLowerCase();
        return hay.includes(term);
      });
    },
    [locale, t],
  );

  const searchTable = useCallback(() => {
    applyToolbarSearch(searchValue);
    setSearchIconColor(String(searchValue || '').trim() ? 'active' : '');
    setVisibleSearch(false);
  }, [applyToolbarSearch, searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setSearchIconColor('');
    applyToolbarSearch('');
  }, [applyToolbarSearch]);

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openAdd('edit', rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function TypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const text = formatCrmSetupApplicableTypes(
      rowData?.applicable_types,
      t,
    );
    if (text === '—') return <span className="block w-full min-w-0">—</span>;
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text}>{text}</span>;
  }

  function NameCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const text = displayCrmStateName(rowData?.name, locale);
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function PickedColorFormatter(props) {
    if (!props || !props.cell) return null;
    const rowData = props.cell.getRow().getData();
    const color = crmSetupStateDisplayHex(rowData?.color);
    if (!color) return null;
    return (
      <div
        style={{
          width: '23px',
          height: '23px',
          backgroundColor: color,
          borderRadius: '4px',
          border: '1px solid #ccc',
          margin: 'auto',
        }}
        title={getSlovakColorName ? getSlovakColorName(color) : color}
      />
    );
  }

  function PhaseFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const option = CRM_STATE_PHASES.find((item) => item.code === rowData?.phase);
    const text = option ? t(option.name) : (rowData?.phase || '');
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function TransitionTypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const option = CRM_STATE_TRANSITION_TYPES.find((item) => item.code === rowData?.transition_type);
    const text = option ? t(option.name) : (rowData?.transition_type || '');
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.created_by_user;
    const text = formatDate
      ? formatDate(rowData?.created_at, dateTimeFmt, true)
      : DateTime.fromISO(String(rowData?.created_at || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.updated_by_user;
    const text = formatDate
      ? formatDate(rowData?.updated_at, dateTimeFmt, true)
      : DateTime.fromISO(String(rowData?.updated_at || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(
    () => [
      {
        field: 'rowSelection',
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        headerSort: false,
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        formatter: reactFormatter(<EditRowFormatter />),
        width: 55,
        hozAlign: 'center',
        headerSort: false,
        frozen: true,
        resizable: false,
        title: '',
        field: '_edit',
        print: false,
        download: false,
      },
      {
        title: t('ServiceTaskTable.Col.Id'),
        field: 'id',
        sorter: 'number',
        minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
        widthGrow: 0.6,
        hozAlign: 'left',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: normalizeFilter,
      },
      {
        title: t('ServiceStateAdd.StateName'),
        field: 'name',
        sorter: (a, b, aRow, bRow) => {
          const da = aRow?.getData?.()?.name ?? a;
          const db = bRow?.getData?.()?.name ?? b;
          const sa = displayCrmStateName(da, locale);
          const sb = displayCrmStateName(db, locale);
          return String(sa).localeCompare(String(sb), undefined, { sensitivity: 'base' });
        },
        minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateAdd.StateName')),
        widthGrow: 1,
        hozAlign: 'left',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: nameHeaderFilter,
        formatter: reactFormatter(<NameCellFormatter />),
      },
      {
        title: t('ServiceStateAdd.ShowIn'),
        field: 'applicable_types',
        sorter: (a, b, aRow, bRow) => {
          const ra = aRow?.getData?.()?.applicable_types ?? a;
          const rb = bRow?.getData?.()?.applicable_types ?? b;
          const sa = formatCrmSetupApplicableTypes(ra, t);
          const sb = formatCrmSetupApplicableTypes(rb, t);
          return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
        },
        minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceStateAdd.ShowIn')),
        widthGrow: 1,
        hozAlign: 'left',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: typesHeaderFilter,
        formatter: reactFormatter(<TypeFormatter />),
      },
      {
        title: t('ServiceStateAdd.Phase'),
        field: 'phase',
        sorter: 'string',
        minWidth: tabulatorColumnMinWidthFromTitle('Fáza'),
        widthGrow: 1,
        hozAlign: 'left',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: phaseHeaderFilter,
        formatter: reactFormatter(<PhaseFormatter />),
      },
      {
        title: t('ServiceStateAdd.TransitionType'),
        field: 'transition_type',
        sorter: 'string',
        minWidth: tabulatorColumnMinWidthFromTitle('Typ prechodu'),
        widthGrow: 1,
        hozAlign: 'left',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: transitionTypeHeaderFilter,
        formatter: reactFormatter(<TransitionTypeFormatter />),
      },
      {
        title: t('ServiceStateAdd.StateColor'),
        field: 'color',
        sorter: 'string',
        minWidth: tabulatorColumnMinWidthFromTitle('Farba stavu'),
        widthGrow: 0.6,
        hozAlign: 'center',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: (hv, rv, rowData) => {
          if (hv == null || String(hv).trim() === '') return true;
          const hex = crmSetupStateDisplayHex(rowData?.color);
          const display = getSlovakColorName ? (getSlovakColorName(hex) || hex) : hex;
          return normalizeFilter(hv, display) || normalizeFilter(hv, hex) || normalizeFilter(hv, String(rowData?.color ?? ''));
        },
        formatter: reactFormatter(<PickedColorFormatter />),
      },
      {
        title: t('CustomerStatesList.Col.Created'),
        field: 'created_at',
        sorter: 'datetime',
        sorterParams: { format: 'iso' },
        hozAlign: 'left',
        minWidth: tabulatorDateUserColumnMinWidth(t('Common.Column.CreatedAt')),
        widthGrow: 1,
        formatter: reactFormatter(<CreatedAtCellFormatter />),
        headerFilter: dateUserHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: dateOrUserFilter,
        headerFilterFuncParams: { name: 'created_at' },
        headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      },
      {
        title: t('ServiceTaskTable.Col.Updated'),
        field: 'updated_at',
        sorter: 'datetime',
        sorterParams: { format: 'iso' },
        hozAlign: 'left',
        minWidth: tabulatorDateUserColumnMinWidth(t('Common.Column.UpdatedAt')),
        widthGrow: 1,
        formatter: reactFormatter(<UpdatedAtCellFormatter />),
        headerFilter: dateUserHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: dateOrUserFilter,
        headerFilterFuncParams: { name: 'updated_at' },
        headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      },
    ],
    [
      dateFmt,
      dateTimeFmt,
      dateOrUserFilter,
      getSlovakColorName,
      locale,
      nameHeaderFilter,
      normalizeFilter,
      phaseHeaderFilter,
      t,
      transitionTypeHeaderFilter,
      typesHeaderFilter,
    ],
  );

  const closeMenu = () => {
    setExpandedKeys({});
  };

  const setMask = (field, value) => {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE, ...columnsVisible, [field]: value };
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  };

  const maskSelectAll = () => {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => {
      next[k] = true;
    });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  };

  const maskDeselectAll = () => {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => {
      next[k] = false;
    });
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  };

  const saveUserMaskColumns = () => {
    const table = getTable();
    const filtersSnapshot = table?.getHeaderFilters?.() ?? [];
    const attributes = table?.getColumnLayout?.() ?? null;
    setUserMaskFilter('CustomerStatesList', 'all', columnsVisible, filtersSnapshot, attributes, null);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('Common.SaveStandardMaskLayoutPrompt'),
      header: t('Common.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = tableRef.current?.table ?? tableRef.current;
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        const colsForSave = mergeVisibility(layout.columns || {});
        setStandardMaskFilter('CustomerStatesList', colsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'CustomerStatesList');
    if (!standard) return;
    const attributes = typeof standard.attributes === 'string' ? (() => { try { return JSON.parse(standard.attributes); } catch { return []; } })() : (standard.attributes || []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const savedCols = typeof standard.columns === 'string' ? (() => { try { return JSON.parse(standard.columns); } catch { return {}; } })() : (standard.columns || {});
    const colsForSave = mergeVisibility(savedCols && typeof savedCols === 'object' ? savedCols : {});
    setUserMaskFilter('CustomerStatesList', 'all', colsForSave, null, attributes, null, () => {
      const table = tableRef.current?.table ?? tableRef.current;
      if (!table) return;
      try { table.setColumnLayout?.(attributes); } catch (_) {}
      setColumnsVisible(colsForSave);
      applyDataColumnVisibilityToTable(table, colsForSave);
    });
  };

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [
    {
      key: '0',
      label: t('ServiceTask.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        {
          key: '0_1',
          label: t('ServiceTask.SetFilter'),
          icon: 'pi pi-filter',
          command: () => {
            const tbl = getTable();
            setUserMaskFilter('CustomerStatesList', 'set_filters', null, tbl?.getHeaderFilters?.() ?? [], null);
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_2',
          label: t('ServiceTask.ClearFilter'),
          icon: 'pi pi-filter-slash',
          command: () => {
            const tbl = getTable();
            setUserMaskFilter('CustomerStatesList', 'set_filters', null, [], null);
            tbl?.clearHeaderFilter?.();
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_3',
          label: t('ServiceTask.SetSort'),
          icon: 'pi pi-sort-alt',
          command: () => {
            const tbl = getTable();
            setUserMaskFilter('CustomerStatesList', 'set_sorters', null, null, null, cleanSorters(tbl?.getSorters?.() ?? []));
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_4',
          label: t('Common.ClearSorting'),
          icon: 'pi pi-sort-alt-slash',
          command: () => {
            const tbl = getTable();
            setUserMaskFilter('CustomerStatesList', 'set_sorters', null, null, null, []);
            tbl?.setSort?.([]);
            closeMenu();
          },
          template: itemRenderer,
        },
        { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); closeMenu(); }, template: itemRenderer },
        ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
        { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences?.('CustomerStatesList'); closeMenu(); }, template: itemRenderer },
      ],
    },
  ];

  const printRows = () => {
    const table = getTable();
    if (!table) return;
    const filters = table.getHeaderFilters?.() ?? [];
    const params = {};
    filters.forEach((f) => {
      const field = f?.field;
      const value = f?.value;
      if (!field || value == null || String(value).trim() === '') return;
      params[`filter_${field}`] = String(value);
    });
    const sorters = cleanSorters(table.getSorters?.() ?? []);
    const printColumns = (table.getColumns?.() ?? [])
      .map((col) => col?.getField?.() ?? col?.getDefinition?.()?.field)
      .filter(
        (field) =>
          field
          && field !== '_edit'
          && Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field)
          && columnsVisible[field] !== false,
      );
    const suffix = buildExportQueryStringFromParams(
      {
        ...params,
        toolbar_search: String(searchValue ?? '').trim() || undefined,
        sorters: JSON.stringify(sorters),
      },
      {
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns,
      },
    );
    setPrintChoiceVisible(false);
    setDialogText?.(t('Common.PreparingPrint'));
    setVisible?.(true);
    fetchTextGet(`/api/crm_states/export/print${suffix}`)
      .then((html) => {
        if (!openPrintHtmlWindow(html)) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 3500 });
        }
      })
      .catch((e) => {
        toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4000 });
      })
      .finally(() => setVisible?.(false));
  };

  const exportRows = async () => {
    const table = getTable();
    if (!table) return;
    const filters = table.getHeaderFilters?.() ?? [];
    const params = {};
    filters.forEach((f) => {
      const field = f?.field;
      const value = f?.value;
      if (!field || value == null || String(value).trim() === '') return;
      params[`filter_${field}`] = String(value);
    });
    const sorters = cleanSorters(table.getSorters?.() ?? []);
    const printColumns = (table.getColumns?.() ?? [])
      .map((col) => col?.getField?.() ?? col?.getDefinition?.()?.field)
      .filter(
        (field) =>
          field
          && field !== '_edit'
          && Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field)
          && columnsVisible[field] !== false,
      );
    const suffix = buildExportQueryStringFromParams(
      {
        ...params,
        toolbar_search: String(searchValue ?? '').trim() || undefined,
        sorters: JSON.stringify(sorters),
      },
      { includePrintColumns: true, printColumns },
    );
    const docTitle = t(EXPORT_DOCUMENT_TITLE_KEYS.CRM_STATES);
    const defaultXlsx = xlsxDownloadFilename(docTitle);
    try {
      setDialogText?.(t('Common.PreparingExport'));
      setVisible?.(true);
      const { blob, filename } = await downloadBlobGet(`/api/crm_states/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4000 });
    } finally {
      setVisible?.(false);
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add">
            <button title={t('Icon.AddNew')} className="disabled:opacity-25" disabled={!canAdd} onClick={() => openAdd('add')} type="button">
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button title={t('Icon.Edit')} className="disabled:opacity-25" onClick={onEditSelected} disabled={editIcon} type="button">
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy">
            <button title={t('Icon.Copy')} className="disabled:opacity-25" onClick={onCopySelected} disabled={copyIcon} type="button">
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete">
            <button title={t('Icon.Delete')} className="disabled:opacity-25" onClick={confirmDelete} disabled={deleteIcon} type="button">
              <DeleteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)} type="button">
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')}
              onClick={searchIconColor === 'active' ? clearSearch : () => setVisibleSearch(true)}
              type="button"
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
              ) : (
                <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button title={t('Common.ExportToExcel')} onClick={exportRows} type="button">
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button title={t('Common.Refresh')} onClick={reloadCatalog} type="button">
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button
              title={t('Icon.First')}
              onClick={() => {
                const table = getTable();
                table?.scrollToRow?.(catalogRows?.[0]?.id, 'top', false);
              }}
              type="button"
            >
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button
              title={t('Icon.Previous')}
              onClick={() => {
                const table = getTable();
                table?.navigatePrev?.();
              }}
              type="button"
            >
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button
              title={t('Icon.Next')}
              onClick={() => {
                const table = getTable();
                table?.navigateNext?.();
              }}
              type="button"
            >
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button
              title={t('Icon.Last')}
              onClick={() => {
                const table = getTable();
                const last = catalogRows?.[catalogRows.length - 1];
                if (last) table?.scrollToRow?.(last.id, 'bottom', false);
              }}
              type="button"
            >
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button
              title={t('Common.AddToFavorites')}
              onClick={() => openFavoriteDialog?.(t('CustomerStatesList.FavoriteTitle'), PAGE_LIST_KEY)}
              type="button"
            >
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="price">
            <button title={t('Common.QuickView')} onClick={() => setFastView?.(PAGE_LIST_KEY)} type="button">
              <PriceIcon />
            </button>
          </div>
          {canSetStandardMask ? (
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="card flex justify-content-center z-[100]">
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                onRef={(r) => { tableRef.current = r?.current; }}
                data={catalogRows}
                columns={columns}
                className="h-[calc(100vh_-_193px)]"
                selectable
                selectableRows
                placeholder={t('CustomerStatesList.NoRows')}
                options={{
                  movableColumns: true,
                  resizableRows: false,
                  pagination: true,
                  paginationSize: 50,
                  layout: 'fitColumns',
                  headerSortTristate: true,
                  filterMode: 'local',
                  sortMode: 'local',
                }}
                events={{
                  rowSelectionChanged: onSelectRow,
                  renderComplete: () => {
                    const selected = getTable()?.getSelectedData?.() ?? [];
                    if (selected.length === 0) {
                      setEditIcon(true);
                      setCopyIcon(true);
                      setDeleteIcon(true);
                    }
                  },
                  tableBuilt: () => {
                    restoreCrmSetupLocalListMask({
                      pageKey: PAGE_LIST_KEY,
                      state,
                      table: getTable(),
                      mergeVisibility,
                      applyDataColumnVisibilityToTable,
                      setColumnsVisible,
                      defaultColumnFields: Object.keys(DEFAULT_COLUMNS_VISIBLE),
                    });
                    const selected = getTable()?.getSelectedData?.() ?? [];
                    onSelectRow(selected);
                  },
                  ...getTabulatorPersistLayoutEvents(PAGE_LIST_KEY, tableRef, setUserMaskFilter),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex items-center gap-3 w-full">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400"
          />
          <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center">
            <SearchIcon />
          </button>
        </div>
      </Dialog>
      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={printRows}
      />
      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('Common.SetMask')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('Common.SelectAll')}
        deselectAllLabel={t('Common.DeselectAll')}
        saveLabel={t('Common.Save')}
        getFieldLabel={(field) => {
          const col = columns.find((item) => item?.field === field);
          return col?.title || field;
        }}
        inputIdPrefix="crm-state-mask"
      />
    </div>
  );
}
